import { LeafletMouseEvent } from 'leaflet';

import { GroupedPinContext } from './clusterEvents';
import { MapRouteMarkerVM } from './mapVM';

/**
 * click ハンドラが受け取るもの。leaflet が直接呼ぶときはマウスイベント、
 * クラスター経由 (forwardClusterEvent) のときは文脈オブジェクトが渡る。
 */
type ClickArg = LeafletMouseEvent | GroupedPinContext | undefined;

const groupedContextOf = (arg: ClickArg): GroupedPinContext | undefined =>
  (arg != null && 'grouped' in arg ? arg : undefined);

type MarkerHandlers = {
  onMarkerRowClick?: (rowId: string) => void;
  onMarkerClick?: (rowId: string, marker: MapRouteMarkerVM) => void;
  /** 右クリックで選択解除。旧地図（/plans）と同じ操作 */
  onMarkerRowDeselect?: (rowId: string) => void;
};

// react-leaflet の Marker eventHandlers はコンポーネントテストが無いと配線漏れを
// 検知できない（brief 参照）。組み立て自体をここに切り出し、純粋関数として保証する。
export function buildRouteMarkerEventHandlers(
  rowId: string,
  marker: MapRouteMarkerVM,
  handlers: MarkerHandlers,
): { click: () => void; contextmenu: () => void } {
  return {
    click: () => {
      handlers.onMarkerRowClick?.(rowId);
      handlers.onMarkerClick?.(rowId, marker);
    },
    contextmenu: () => handlers.onMarkerRowDeselect?.(rowId),
  };
}

type UnassignedHandlers = {
  /** context は集合状態のときだけ渡る。詳細を出さない判断に使う */
  onUnassignedClick?: (id: string, context?: GroupedPinContext) => void;
  /** 右クリックで選択解除。旧地図（/plans）と同じ操作 */
  onUnassignedDeselect?: (id: string) => void;
};

/**
 * 未割当ピンのイベント配線。
 *
 * ピン 1 本は「地点 1 つ」で、そこにぶら下がる案件は複数ありうる
 * （logposeTech/log_web_issues#2430。案件ごとにピンを立てるとクラスターの丸が
 * 同じ点の配車済みマーカーを覆って押せなくなる）。旧地図
 * （PlanningMapOrderMarker.presenter.tsx:61-75）と同じく、押したらその地点の案件を
 * 全部動かす。
 *
 * 複数ぶら下がっているときは、クラスター経由と同じく「集合」として扱って詳細を
 * 開かせない（まとまっている案件のうち 1 件だけの詳細が出ると紛らわしい）。
 */
export function buildUnassignedMarkerEventHandlers(
  ids: readonly string[],
  handlers: UnassignedHandlers,
): { click: (arg?: ClickArg) => void; contextmenu: () => void } {
  return {
    click: (arg) => {
      const context = groupedContextOf(arg) ?? (ids.length > 1 ? { grouped: true } as const : undefined);
      ids.forEach((id) => handlers.onUnassignedClick?.(id, context));
    },
    contextmenu: () => ids.forEach((id) => handlers.onUnassignedDeselect?.(id)),
  };
}
